import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, Play, Pause, ArrowDown } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { useStreamEvent } from "../lib/sse";
import { PageHeader } from "../components/PageScaffold";
import { Button, Pill } from "../components/primitives";
import { shortTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { LogEntry } from "../lib/types";

type Level = "all" | "info" | "warn" | "error";

const MAX_BUFFER = 1000;

export default function Logs() {
  const [level, setLevel] = useState<Level>("all");
  const [search, setSearch] = useState("");
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Seed with the most recent N entries; SSE keeps it live
  const { data: initial } = useQuery({
    queryKey: qk.bridge.logs({ limit: 250 }),
    queryFn: () => BridgeApi.logs({ limit: 250 }),
  });

  const [buffer, setBuffer] = useState<LogEntry[]>([]);

  useEffect(() => {
    if (initial && buffer.length === 0) setBuffer(initial);
  }, [initial, buffer.length]);

  useStreamEvent("log", (ev) => {
    if (ev.type !== "log") return;
    if (paused) return;
    setBuffer((prev) => {
      const next = [...prev, ev.entry];
      return next.length > MAX_BUFFER ? next.slice(next.length - MAX_BUFFER) : next;
    });
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return buffer.filter((e) => {
      if (level !== "all" && e.level !== level) return false;
      if (!q) return true;
      return (
        e.msg.toLowerCase().includes(q) ||
        (e.fields ? JSON.stringify(e.fields).toLowerCase().includes(q) : false)
      );
    });
  }, [buffer, level, search]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filtered, autoScroll]);

  // Detect if user scrolled away from bottom → disable autoScroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
      if (!nearBottom && autoScroll) setAutoScroll(false);
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [autoScroll]);

  const counts = useMemo(() => {
    const c = { info: 0, warn: 0, error: 0 };
    for (const e of buffer) c[e.level]++;
    return c;
  }, [buffer]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Logs"
        icon={ScrollText}
        subtitle={`${buffer.length}/${MAX_BUFFER} buffered · ${filtered.length} shown · ${counts.info}i ${counts.warn}w ${counts.error}e`}
        actions={
          <>
            <Button size="sm" variant={paused ? "primary" : "secondary"} onClick={() => setPaused(!paused)}>
              {paused ? <><Play className="size-3.5" />Resume</> : <><Pause className="size-3.5" />Pause</>}
            </Button>
            {!autoScroll && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setAutoScroll(true);
                  if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                }}
              >
                <ArrowDown className="size-3.5" />
                Tail
              </Button>
            )}
          </>
        }
      />
      <div className="flex-1 min-h-0 mx-5 mb-5 panel flex flex-col overflow-hidden">
        <div className="border-b border-border px-3 py-2 flex items-center gap-2">
          <input
            placeholder="search logs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs flex-1"
          />
          <div className="flex items-center gap-1">
            {(["all", "info", "warn", "error"] as Level[]).map((l) => (
              <button
                key={l}
                onClick={() => setLevel(l)}
                className={cn(
                  "h-7 px-2.5 rounded text-[11px] font-medium transition-colors",
                  level === l
                    ? l === "error" ? "bg-danger-subtle text-danger" :
                      l === "warn" ? "bg-warning-subtle text-warning" :
                      l === "info" ? "bg-info-subtle text-info" :
                      "bg-surface-2 text-fg"
                    : "text-fg-muted hover:bg-surface-2 hover:text-fg",
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto font-mono text-[11px] leading-snug">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-fg-muted text-sm">No logs match the filter.</div>
          ) : (
            <div className="px-3 py-2 space-y-0.5">
              {filtered.map((e, i) => (
                <LogLine key={i} entry={e} />
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-border px-3 py-1.5 flex items-center justify-between text-[10px] text-fg-subtle">
          <span>
            {paused ? (
              <Pill tone="warning">paused</Pill>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-success animate-pulse-dot" />
                streaming
              </span>
            )}
          </span>
          <span>autoscroll: {autoScroll ? "on" : "off"}</span>
        </div>
      </div>
    </div>
  );
}

function LogLine({ entry }: { entry: LogEntry }) {
  const tone =
    entry.level === "error" ? "bg-danger-subtle/30 border-l-danger" :
    entry.level === "warn" ? "bg-warning-subtle/30 border-l-warning" :
    "border-l-border";
  const tagTone =
    entry.level === "error" ? "bg-danger-subtle text-danger" :
    entry.level === "warn" ? "bg-warning-subtle text-warning" :
    "bg-info-subtle text-info";
  return (
    <div className={cn("flex items-start gap-2 px-2 py-0.5 border-l-2 rounded-r hover:bg-surface-2/40", tone)}>
      <span className="text-fg-subtle tabular-nums w-12 flex-shrink-0">{shortTime(entry.ts)}</span>
      <span className={cn("inline-flex items-center justify-center min-w-[3rem] h-4 rounded text-[9px] uppercase font-semibold tracking-wider", tagTone)}>
        {entry.level}
      </span>
      <span className="text-fg flex-1 min-w-0 break-words">
        {entry.msg}
        {entry.fields && Object.keys(entry.fields).length > 0 && (
          <span className="ml-1.5 text-fg-subtle">{JSON.stringify(entry.fields)}</span>
        )}
      </span>
    </div>
  );
}
